import {type TaskChecklist} from '@domain/hroc-checklists'
import {CircularProgress, Link, Stack} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {generatePath, Link as RouterLink} from 'react-router'

import {
  HROC_CHECKLIST_KIND_TRANSLATION_KEYS,
  HROC_CHECKLISTS_WIDGET_NAMESPACE,
  HrocChecklistsPathSegments
} from '../consts'
import {RefererName} from '../types'

import {ChecklistStatusChip} from './ChecklistStatusChip'

type Props = {
  checklists: TaskChecklist[]
  isLoading: boolean
  plantId: string
}

const EMPTY_PLACEHOLDER = '-'

/**
 * Renders a list of task checklists with links to the checklist detail page.
 *
 * **Important:** This component navigates to `HrocChecklistsPathSegments.Checklist`.
 * The consuming feature/app must register the `hrocChecklistsSubRoute` (from
 * `@features/hroc-checklists`) as a child route so that the checklist page is
 * reachable and "back to" navigation works correctly.
 *
 * @example
 * // In your feature's route definition:
 * import {hrocChecklistsSubRoute} from '@features/hroc-checklists'
 *
 * const myFeatureRoute: RouteObject = {
 *   path: '*',
 *   children: [
 *     {index: true, element: <MyFeaturePage />}, // uses EventTaskChecklists
 *     hrocChecklistsSubRoute
 *   ]
 * }
 */
export const EventTaskChecklists = ({checklists, isLoading, plantId}: Props) => {
  if (isLoading) {
    return <CircularProgress size={20} />
  }
  if (!checklists?.length) {
    return <>{EMPTY_PLACEHOLDER}</>
  }

  return (
    <Stack spacing={2}>
      {checklists.map((checklist) => (
        <ChecklistItem key={checklist.id} checklists={checklist} plantId={plantId} />
      ))}
    </Stack>
  )
}

const ChecklistItem = ({checklists, plantId}: {checklists: TaskChecklist; plantId: string}) => {
  const {t} = useTranslation(HROC_CHECKLISTS_WIDGET_NAMESPACE)

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Link
        component={RouterLink}
        to={{
          pathname: generatePath(HrocChecklistsPathSegments.Checklist, {
            checklistId: checklists.id
          }),
          search: `?refererName=${RefererName.Tasks}`
        }}
        state={{plantId}}
      >
        {t(HROC_CHECKLIST_KIND_TRANSLATION_KEYS[checklists.kind])}
      </Link>
      <ChecklistStatusChip status={checklists.status} size="small" />
    </Stack>
  )
}
